// Misc
import React, { PureComponent, createElement } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import keys from 'lodash/keys'
import { withStyles } from '@material-ui/core/styles'

// Validation
import { Validator } from 'react-vld'
import { validateRequiredString, validateMaxLength, validateEmail, validateMinLength, validateURL } from 'utils/validation'

// Atoms
import Input, { InputPropTypes } from 'atoms/Input'

const placeholders = style => ({
  '&::placeholder': style,
  '&::-webkit-input-placeholder': style,
  '&::-moz-placeholder': style,
  '&:-ms-input-placeholder': style,
  '&:-moz-placeholder': style,
})

const styles = theme => ({
  root: {
    position: 'relative',
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.body2.fontFamily,
    fontWeight: theme.typography.body2.fontWeight,
  },
  rootWithError: {
    '& $input': {
      color: theme.palette.error.main,
      ...placeholders({
        color: theme.palette.error.main,
      })
    },
  },
  input: {
    position: 'relative',
    display: 'block',
    height: '2rem',
    width: '100%',
    font: 'inherit',
    background: 'none',
    border: 0,
    margin: 0,
    padding: 0,
    borderRadius: 0,
    color: theme.palette.text.primary,
    outline: 0,
    transition: theme.transitions.create('color'),
    ...placeholders({
      color: theme.palette.text.secondary,
      transition: theme.transitions.create('color')
    })
  },
  multilineInput: {
    resize: 'none',
    overflow: 'hidden',
    minHeight: '2rem',
    lineHeight: '1.2rem',
    paddingTop: '0.4rem',
    paddingBottom: '0.4rem',
  },
  valueAddon: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
  },
  valueAddonValue: {
    visibility: 'hidden',
    flexShrink: 0,
  },
})

const TextInputPropTypes = {
  classes: PropTypes.object.isRequired,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  validation: PropTypes.object,
  inputRef: PropTypes.func,
  maxLength: PropTypes.number,
  onSave: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onEnterPress: PropTypes.func,
  onPaste: PropTypes.func,
  multiline: PropTypes.bool,
  enableLineBreak: PropTypes.bool,
  valueAddon: PropTypes.any,
  errorMessage: PropTypes.any,
  type: PropTypes.string,
  showErrorMessage: PropTypes.bool,
}

@withStyles(styles)
class TextInput extends PureComponent {
  static propTypes = TextInputPropTypes

  static defaultProps = {
    defaultValue: '',
    type: 'text',
    inputRef: () => {},
    onChange: () => {},
    onBlur: () => {},
    onKeyPress: () => {},
    onPaste: () => {},
    onEnterPress: () => {},
    showErrorMessage: true,
  }

  constructor({ defaultValue, value }) {
    super()
    this.saveNeeded = false
    this.changedByPaste = false
    this.state = {
      value: value || defaultValue,
    }
  }

  static getDerivedStateFromProps({ value }, prevState) {
    if (value !== undefined && value !== prevState.value) {
      return { value }
    }
    return null
  }

  componentDidMount() {
    this.updateHeight()
  }

  componentDidUpdate(prevProps, prevState) {
    const { value } = this.state
    if (prevState.value !== value) {
      this.updateHeight()
    }
  }

  componentWillUnmount() {
    this.dropSaveTimeout()
  }

  getErrorMessage = () => {
    const { validation = {}, errorMessage } = this.props
    return validation.isInvalid ? validation.error.message : errorMessage
  }

  updateHeight = () => {
    const { multiline } = this.props
    if (multiline) {
      this.input.style.height = '1px'
      this.input.style.height = `${this.input.scrollHeight}px`
    }
  }

  triggerSave = () => {
    const { value } = this.state
    const { validation, onSave } = this.props
    const { isValid } = validation ? validation.validate() : {}
    if (onSave) {
      if (this.saveNeeded && isValid) {
        this.dropSaveTimeout()
        this.saveNeeded = false
        onSave(value)
      }
    }
  }

  createSaveTimeout = () => {
    this.dropSaveTimeout()
    this.saveTimeout = setTimeout(this.triggerSave, 1000)
  }

  dropSaveTimeout = () => {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  }

  handlePaste = (ev) => {
    const { onPaste } = this.props
    this.changedByPaste = true
    onPaste(ev)
  }

  handleChange = (ev) => {
    const { value: stateValue } = this.state
    const { onChange, validation, maxLength, value: propValue } = this.props
    const { value } = ev.target

    // Prevent change when typing value longer than maxLength
    if (
      maxLength
      && !this.changedByPaste
      && value.length > maxLength
      && value.length > stateValue.length
    ) {
      return
    }

    this.changedByPaste = false
    this.saveNeeded = true
    onChange(ev)

    // Reset validation
    if (validation) validation.reset({ updateComponent: false })

    // Update state if only input not controlled
    if (propValue === undefined) {
      this.setState({ value }, this.createSaveTimeout)
    } else {
      this.createSaveTimeout()
    }
  }

  handleBlur = (ev) => {
    const { onBlur } = this.props
    onBlur(ev)
    this.triggerSave()
  }

  handleKeyPress = (ev) => {
    const { onKeyPress, onEnterPress, multiline, enableLineBreak } = this.props
    onKeyPress(ev)
    if (ev.key === 'Enter') {
      onEnterPress(ev)
      // Prevent new line
      if (multiline && !enableLineBreak) ev.preventDefault()
      this.triggerSave()
    }
  }

  inputRef = (input) => {
    const { inputRef } = this.props
    this.input = input
    inputRef(input)
  }

  renderValueAddon() {
    const { valueAddon, classes, multiline } = this.props
    const { value } = this.state
    return !!valueAddon && !!value && !multiline && (
      <div className={classes.valueAddon}>
        <div>
          <span className={classes.valueAddonValue}>{ value }</span>
          { valueAddon }
        </div>
      </div>
    )
  }

  renderInput(id) {
    const props = omit(this.props, keys(TextInputPropTypes), keys(InputPropTypes))
    const { classes, multiline, type } = this.props
    const { value } = this.state

    const cx = classNames(classes.input, {
      [classes.multilineInput]: multiline,
    })

    const element = multiline ? 'textarea' : 'input'

    const elementProps = {
      ...props,
      id,
      type,
      value,
      className: cx,
      onBlur: this.handleBlur,
      onPaste: this.handlePaste,
      onChange: this.handleChange,
      onKeyPress: this.handleKeyPress,
      ref: this.inputRef,
    }

    return createElement(element, elementProps)
  }

  render() {
    const inputProps = pick(this.props, keys(InputPropTypes))
    const errorMessage = this.getErrorMessage()
    const { classes, showErrorMessage } = this.props
    const cx = classNames(classes.root, {
      [classes.rootWithError]: !!errorMessage,
    })
    return (
      <Input
        {...inputProps}
        errorMessage={showErrorMessage && errorMessage}
      >
        { ({ id }) => (
          <div className={cx}>
            { this.renderValueAddon() }
            { this.renderInput(id) }
          </div>
        ) }
      </Input>
    )
  }
}

export default class TextInputValidator extends PureComponent { // eslint-disable-line
  static propTypes = {
    validation: PropTypes.object,
    required: PropTypes.bool,
    email: PropTypes.bool,
    url: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    innerRef: PropTypes.func,
  }

  static defaultProps = {
    innerRef: () => {}
  }

  validate = () => {
    const { required, minLength, maxLength, email, url } = this.props
    const { value } = this.textInput.state
    if (required) validateRequiredString(value, 'Required field')
    if (minLength) validateMinLength(value, minLength, 'Value too short')
    if (maxLength) validateMaxLength(value, maxLength, 'Value too long')
    if (email) validateEmail(value, 'Invalid email')
    if (url) validateURL(value, 'Invalid URL')
  }

  innerRef = (component) => {
    const { innerRef } = this.props
    this.textInput = component
    innerRef(component)
  }

  render() {
    const { validation, innerRef, minLength, url, email, required, ...rest } = this.props
    return (
      <Validator rule={this.validate}>
        { innerValidation => (
          <TextInput
            {...rest}
            validation={validation || innerValidation}
            innerRef={this.innerRef}
          />
        ) }
      </Validator>
    )
  }
}
